<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useKeyboardInset } from "@/composables/useKeyboardInset";
import { scrollerOf, tappedBlankFor } from "@/utils/keyboard";
import { blankKey } from "@/utils/blanks";

// Fills the make's blanks one at a time from a small card floating at the bottom: the
// document scrolls to each blank, answers show in place as they're typed, and closing
// the card puts them into the make. On phones it floats just above the keyboard.
const props = defineProps({
  blanks: { type: Array, required: true },
  modelValue: { type: Object, required: true },
});
const emit = defineEmits(["update:modelValue", "done"]);

const store = useStore();
const active = computed(() => store.state.result.fillingBlank);
const index = computed(() => Math.max(0, props.blanks.indexOf(active.value)));
const last = computed(() => index.value >= props.blanks.length - 1);
const keyboard = useKeyboardInset();
const isMobile = computed(() => store.state.isMobile);

const input = ref(null);
const bar = ref(null);
const value = computed({
  get: () => props.modelValue[active.value] || "",
  set: (text) =>
    emit("update:modelValue", { ...props.modelValue, [active.value]: text }),
});

const go = (to) => {
  const blank = props.blanks[to];
  if (blank) store.commit("result/SET_FILLING_BLANK", blank);
};
const next = () => (last.value ? emit("done") : go(index.value + 1));

// Keep the blank being filled in view: clear of the top of the page and of this bar
// and the keyboard under it. Nothing moves if it can already be seen.
const showBlank = () => {
  const target =
    tappedBlankFor(active.value) ||
    document.querySelector(
      `#shepherd-target-make [data-blank-key="${CSS.escape(
        blankKey(active.value)
      )}"]`
    );
  if (!target) return;
  const viewport = window.visualViewport;
  const top = viewport ? viewport.offsetTop : 0;
  const bottom = top + (viewport ? viewport.height : window.innerHeight);
  const clearTop = top + 72;
  const clearBottom =
    Math.min(bottom, bar.value?.getBoundingClientRect().top ?? bottom) - 16;
  const rect = target.getBoundingClientRect();
  if (rect.top >= clearTop && rect.bottom <= clearBottom) return;
  // A third of the way down the clear space reads naturally
  const goal = clearTop + Math.max(0, clearBottom - clearTop) * 0.3;
  scrollerOf(target).scrollBy({ top: rect.top - goal, behavior: "smooth" });
};

const reveal = async () => {
  await nextTick();
  // Taps on a blank already focus the box (so a phone's keyboard opens); this covers
  // the rest, such as Next
  if (document.activeElement !== input.value)
    input.value?.focus({ preventScroll: true });
  showBlank();
};
watch(active, reveal);
onMounted(reveal);
// The keyboard opening covers the lower part of the screen, so look again
watch(keyboard, () => nextTick(showBlank));
</script>

<template>
  <!-- On phones it's pinned to the screen, so it lives outside the page's layout -->
  <Teleport to="body" :disabled="!isMobile">
    <div
      ref="bar"
      class="fixed inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-40 lg:sticky lg:inset-x-auto lg:bottom-4 lg:z-20 lg:mt-4"
      :style="keyboard ? { bottom: `${keyboard + 8}px` } : undefined"
    >
      <form
        class="mx-auto w-full max-w-xl rounded-2xl bg-primary px-3 pb-2.5 pt-2 text-white shadow-[0_10px_30px_rgba(35,31,66,0.35)]"
        aria-label="Fill in the blanks"
        @submit.prevent="next"
        @keydown.esc.prevent="emit('done')"
      >
        <div class="flex items-center gap-2 pl-1">
          <font-awesome-icon
            :icon="['fas', 'pencil']"
            class="text-xs text-accentSecondary"
          />
          <label
            for="blank-filler-input"
            class="min-w-0 flex-1 truncate text-sm font-bold"
            >{{ active }}</label
          >
          <span class="shrink-0 text-xs text-secondaryLight"
            >{{ index + 1 }} of {{ blanks.length }}</span
          >
          <button
            type="button"
            class="-my-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-secondaryLight hover:bg-white/10 hover:text-white"
            aria-label="Done filling in blanks"
            title="Done"
            @click="emit('done')"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>
        <div class="mt-1.5 flex items-center gap-2">
          <input
            id="blank-filler-input"
            ref="input"
            v-model="value"
            type="text"
            autocomplete="off"
            enterkeyhint="next"
            :placeholder="active"
            class="min-w-0 flex-1 rounded-xl border-0 bg-white px-3 py-2 text-base text-primary placeholder:text-secondaryLight focus:outline-none focus:ring-2 focus:ring-accentSecondary"
          />
          <button
            type="button"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-white/20 disabled:opacity-30"
            :disabled="index === 0"
            aria-label="Previous blank"
            @mousedown.prevent
            @click="go(index - 1)"
          >
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
          </button>
          <button
            type="submit"
            @mousedown.prevent
            class="flex h-9 shrink-0 items-center justify-center rounded-full bg-accentSecondary px-4 text-sm font-bold text-primary transition-colors hover:bg-yellow-300"
          >
            {{ last ? "Done" : "Next" }}
            <font-awesome-icon
              v-if="!last"
              :icon="['fas', 'chevron-right']"
              class="ml-1.5 text-xs"
            />
          </button>
        </div>
      </form>
    </div>
  </Teleport>
</template>
